import { useEffect } from "react";

import { tw } from "classnames";

/**
 * Live region announcement utility for screen readers
 * Provides centralized management of aria-live announcements
 */

const styles = {
  liveRegion: tw(
    "absolute",
    "w-px",
    "h-px",
    "overflow-hidden",
    "whitespace-nowrap",
    "border-0",
    "m-0",
    "p-0"
  ),
} as const;

export class LiveRegionAnnouncer {
  // eslint-disable-next-line no-use-before-define
  private static instance: LiveRegionAnnouncer;
  private politeRegion: HTMLElement | null = null;
  private assertiveRegion: HTMLElement | null = null;
  private debounceTimeout: number | null = null;

  private constructor() {
    this.createLiveRegions();
  }

  static getInstance(): LiveRegionAnnouncer {
    if (!LiveRegionAnnouncer.instance) {
      LiveRegionAnnouncer.instance = new LiveRegionAnnouncer();
    }
    return LiveRegionAnnouncer.instance;
  }

  private createLiveRegions(): void {
    if (typeof document === "undefined") return;

    // Create polite region
    this.politeRegion = document.getElementById("aria-live-polite");
    if (!this.politeRegion) {
      this.politeRegion = document.createElement("div");
      this.politeRegion.id = "aria-live-polite";
      this.politeRegion.setAttribute("aria-live", "polite");
      this.politeRegion.setAttribute("aria-atomic", "true");
      this.politeRegion.className = styles.liveRegion;
      this.politeRegion.style.left = "-10000px";
      this.politeRegion.style.clip = "rect(0, 0, 0, 0)";
      document.body.appendChild(this.politeRegion);
    }

    // Create assertive region
    this.assertiveRegion = document.getElementById("aria-live-assertive");
    if (!this.assertiveRegion) {
      this.assertiveRegion = document.createElement("div");
      this.assertiveRegion.id = "aria-live-assertive";
      this.assertiveRegion.setAttribute("aria-live", "assertive");
      this.assertiveRegion.setAttribute("aria-atomic", "true");
      this.assertiveRegion.className = styles.liveRegion;
      this.assertiveRegion.style.left = "-10000px";
      this.assertiveRegion.style.clip = "rect(0, 0, 0, 0)";
      document.body.appendChild(this.assertiveRegion);
    }
  }

  /**
   * Announce a message to screen readers
   * @param message - The message to announce
   * @param priority - 'polite' (default) or 'assertive' for urgent messages
   * @param debounceMs - Debounce delay in milliseconds (default: 100ms)
   */
  announce(
    message: string,
    priority: "polite" | "assertive" = "polite",
    debounceMs: number = 100
  ): void {
    const targetRegion =
      priority === "assertive" ? this.assertiveRegion : this.politeRegion;
    if (!targetRegion) return;

    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout);
    }

    // Debounce the announcement to prevent flooding screen readers
    this.debounceTimeout = window.setTimeout(() => {
      if (targetRegion) {
        // Only clear and set content in one operation to minimize DOM manipulation
        targetRegion.textContent = message;
      }
      this.debounceTimeout = null;
    }, debounceMs);
  }

  /**
   * Clean up resources and clear timeouts
   */
  cleanup(): void {
    if (this.debounceTimeout) {
      window.clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  }

  /**
   * Announce cart-related updates
   */
  announceCartUpdate(
    action: "added" | "removed" | "updated",
    itemName: string
  ): void {
    const messages = {
      added: `Added ${itemName} to cart`,
      removed: `Removed ${itemName} from cart`,
      updated: `Updated ${itemName} in cart`,
    };
    this.announce(messages[action]);
  }

  /**
   * Announce carousel navigation changes
   */
  announceCarouselNavigation(currentSlide: number, totalSlides: number): void {
    this.announce(`Slide ${currentSlide + 1} of ${totalSlides}`);
  }

  /**
   * Announce modal/drawer state changes
   */
  announceModalState(action: "opened" | "closed", modalName: string): void {
    const messages = {
      opened: `${modalName} opened`,
      closed: `${modalName} closed`,
    };
    this.announce(messages[action]);
  }

  /**
   * Announce cart errors to screen readers with assertive priority
   * @param message - The localized error message to announce
   */
  announceCartError(message: string): void {
    if (!message) return;
    this.announce(message, "assertive");
  }
}

/**
 * Hook for using live region announcements
 * Returns methods for announcing different types of updates
 */
export function useLiveRegion(): {
  announce: (
    message: string,
    priority?: "polite" | "assertive",
    debounceMs?: number
  ) => void;
  announceCarouselNavigation: (
    currentSlide: number,
    totalSlides: number
  ) => void;
  announceCartError: (message: string) => void;
  announceCartUpdate: (
    action: "added" | "removed" | "updated",
    itemName: string
  ) => void;
  announceModalState: (action: "opened" | "closed", modalName: string) => void;
} {
  const announcer = LiveRegionAnnouncer.getInstance();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      announcer.cleanup();
    };
  }, [announcer]);

  return {
    announce: announcer.announce.bind(announcer),
    announceCartError: announcer.announceCartError.bind(announcer),
    announceCartUpdate: announcer.announceCartUpdate.bind(announcer),
    announceCarouselNavigation:
      announcer.announceCarouselNavigation.bind(announcer),
    announceModalState: announcer.announceModalState.bind(announcer),
  };
}
